import { useEffect, useRef } from 'react';
import { useEventBus } from './EventBusContext';
import { EventSubscriber, EventType } from './EventBusContext.type';

/**
 * Hook to subscribe to an event with automatic cleanup on unmount
 * @param eventType - The event type to subscribe to
 * @param callback - The callback function to execute when the event is published
 * @param deps - Optional dependency array to re-subscribe when dependencies change
 */
export const useEventSubscription = <T = any>(
  eventType: EventType,
  callback: EventSubscriber<T>,
  deps: React.DependencyList = []
) => {
  const { subscribe } = useEventBus();
  const callbackRef = useRef(callback);

  // Keep callback ref updated
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    // Subscribe with stable callback that uses the ref
    const unsubscribe = subscribe(eventType, (payload: T) => {
      callbackRef.current(payload);
    });

    // Cleanup on unmount or when dependencies change
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventType, subscribe, ...deps]);
};

